
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  BarChart as BarChartIcon,
  LineChart as LineChartIcon,
  BadgeDollarSign,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const ingredientCostData = [
  { name: 'Jan', current: 3200, projected: 3400 },
  { name: 'Feb', current: 3300, projected: 3450 },
  { name: 'Mar', current: 3500, projected: 3650 },
  { name: 'Apr', current: 3450, projected: 3700 },
  { name: 'May', current: 3600, projected: 3900 },
  { name: 'Jun', current: 3750, projected: 4100 },
];

const cogsData = [
  { name: 'Jan', value: 28 },
  { name: 'Feb', value: 27 },
  { name: 'Mar', value: 29 },
  { name: 'Apr', value: 30 },
  { name: 'May', value: 32 },
  { name: 'Jun', value: 33 },
  { name: 'Jul', value: 34 },
  { name: 'Aug', value: 32 },
  { name: 'Sep', value: 30 },
  { name: 'Oct', value: 29 },
  { name: 'Nov', value: 28 },
  { name: 'Dec', value: 27 },
];

const seasonalCashFlowData = [
  { name: 'Jan', cashFlow: 8500, purchasing: 3200 },
  { name: 'Feb', cashFlow: 9200, purchasing: 3300 },
  { name: 'Mar', cashFlow: 10500, purchasing: 3500 },
  { name: 'Apr', cashFlow: 11000, purchasing: 3450 },
  { name: 'May', cashFlow: 12500, purchasing: 3600 },
  { name: 'Jun', cashFlow: 14000, purchasing: 3750 },
  { name: 'Jul', cashFlow: 15500, purchasing: 4100 },
  { name: 'Aug', cashFlow: 15000, purchasing: 4200 },
  { name: 'Sep', cashFlow: 14000, purchasing: 3800 },
  { name: 'Oct', cashFlow: 12000, purchasing: 3600 },
  { name: 'Nov', cashFlow: 10500, purchasing: 3400 },
  { name: 'Dec', cashFlow: 9500, purchasing: 3300 },
];

const benchmarkData = [
  { kpi: 'COGS', yourValue: 32, industryAvg: 28, benchmark: 25 },
  { kpi: 'Labor Cost', yourValue: 28, industryAvg: 30, benchmark: 27 },
  { kpi: 'Profit Margin', yourValue: 15, industryAvg: 18, benchmark: 22 },
  { kpi: 'Food Waste', yourValue: 7, industryAvg: 5, benchmark: 3 },
  { kpi: 'Average Order', yourValue: 24, industryAvg: 22, benchmark: 26 },
];

const marginErosionData = [
  { item: 'Cherne a la Plancha', currentMargin: 62, previousMargin: 68, change: -6 },
  { item: 'Solomillo de Cabra', currentMargin: 58, previousMargin: 65, change: -7 },
  { item: 'Pasta con Mojo', currentMargin: 70, previousMargin: 72, change: -2 },
  { item: 'Caldo de Pescado', currentMargin: 75, previousMargin: 78, change: -3 },
  { item: 'Hamburguesa de Ternera Canaria', currentMargin: 64, previousMargin: 71, change: -7 },
];

const scenarioItems = [
  { id: 1, name: 'Aumento de Precio del Menú (5%)', impact: '+$4,250', cogs: '-2.1%', margin: '+3.2%' },
  { id: 2, name: 'Cambio de Proveedor (Verduras)', impact: '+$1,850', cogs: '-0.9%', margin: '+1.5%' },
  { id: 3, name: 'Eliminar 3 Platos de Bajo Margen', impact: '+$2,100', cogs: '-1.2%', margin: '+1.8%' },
  { id: 4, name: 'Ajuste de Menú Estacional', impact: '+$3,700', cogs: '-1.8%', margin: '+2.5%' },
  { id: 5, name: 'Estrategia de Compra al Por Mayor', impact: '+$1,400', cogs: '-0.7%', margin: '+1.1%' },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const FinancialPerformanceForecaster = () => {
  const [activeScenario, setActiveScenario] = useState('1');
  const [whatIfValues, setWhatIfValues] = useState({
    menu_price: 0,
    ingredient_cost: 0,
    labor_cost: 0,
    promotion: 0
  });

  const updateWhatIfValue = (key: string, value: string) => {
    setWhatIfValues({
      ...whatIfValues,
      [key]: parseFloat(value) || 0
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Financial Performance Forecaster</h1>
          <p className="text-muted-foreground">Predict costs, analyze financial scenarios, and benchmark performance</p>
        </div>
      </div>

      <Tabs defaultValue="ingredient_cost" className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="ingredient_cost" className="flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            Ingredient Costs
          </TabsTrigger>
          <TabsTrigger value="what_if" className="flex items-center">
            <LineChartIcon className="h-4 w-4 mr-2" />
            What-If Scenarios
          </TabsTrigger>
          <TabsTrigger value="cash_flow" className="flex items-center">
            <DollarSign className="h-4 w-4 mr-2" />
            Cash Flow
          </TabsTrigger>
          <TabsTrigger value="benchmarks" className="flex items-center">
            <BarChartIcon className="h-4 w-4 mr-2" />
            KPI Benchmarks
          </TabsTrigger>
          <TabsTrigger value="margin_erosion" className="flex items-center">
            <AlertCircle className="h-4 w-4 mr-2" />
            Margin Alerts
          </TabsTrigger>
        </TabsList>

        {/* Ingredient Cost Projection Tab */}
        <TabsContent value="ingredient_cost" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Projected Ingredient Cost Changes</CardTitle>
                <CardDescription>
                  6-month forecast of ingredient costs and impact on COGS
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={ingredientCostData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="current" 
                        name="Current Costs" 
                        stroke="#8884d8" 
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="projected" 
                        name="Projected Costs" 
                        stroke="#ff7300" 
                        strokeDasharray="5 5" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Impact on COGS</CardTitle>
                <CardDescription>
                  Monthly COGS percentage based on projected costs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Current COGS</p>
                      <p className="text-2xl font-bold">28%</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Projected COGS</p>
                      <p className="text-2xl font-bold text-amber-500">32%</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Biggest Impact Categories</p>
                    <div className="grid gap-2">
                      <div className="flex justify-between items-center">
                        <span>Seafood</span>
                        <Badge variant="destructive">+15%</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Dairy</span>
                        <Badge variant="destructive">+8%</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Produce</span>
                        <Badge variant="secondary">+3%</Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button variant="outline" className="w-full">View Detailed Breakdown</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={cogsData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[25, 35]} />
                <Tooltip formatter={(value) => [`${value}%`, 'COGS']} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  name="COGS %" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }} 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* What-If Scenarios Tab */}
        <TabsContent value="what_if" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>What-If Scenario Builder</CardTitle>
                <CardDescription>
                  Create financial scenarios to see potential impacts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="menu_price">Menu Price Adjustment (%)</Label>
                      <Input 
                        id="menu_price" 
                        type="number" 
                        placeholder="0" 
                        onChange={(e) => updateWhatIfValue('menu_price', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ingredient_cost">Ingredient Cost Change (%)</Label>
                      <Input 
                        id="ingredient_cost" 
                        type="number" 
                        placeholder="0" 
                        onChange={(e) => updateWhatIfValue('ingredient_cost', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="labor_cost">Labor Cost Change (%)</Label>
                      <Input 
                        id="labor_cost" 
                        type="number" 
                        placeholder="0" 
                        onChange={(e) => updateWhatIfValue('labor_cost', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="promotion">Promotional Discount (%)</Label>
                      <Input 
                        id="promotion" 
                        type="number" 
                        placeholder="0" 
                        onChange={(e) => updateWhatIfValue('promotion', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <Button className="w-full">Calculate Impact</Button>
                  </div>
                  
                  <div className="pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium">Revenue Impact</span>
                      <span className="font-bold text-green-500">+$4,250</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">COGS Change</span>
                      <span className="font-bold text-amber-500">+1.5%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Profit Margin</span>
                      <span className="font-bold text-green-500">+2.3%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="flex flex-col">
              <CardHeader>
                <CardTitle>Saved Scenarios</CardTitle>
                <CardDescription>
                  Previously calculated scenarios
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <Tabs value={activeScenario} onValueChange={setActiveScenario} className="h-full flex flex-col">
                  <TabsList className="grid grid-cols-5">
                    {[1, 2, 3, 4, 5].map(id => (
                      <TabsTrigger key={id} value={id.toString()}>
                        #{id}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  
                  <div className="mt-4 flex-1 overflow-auto">
                    {scenarioItems.map(item => (
                      <TabsContent key={item.id} value={item.id.toString()} className="h-full">
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg">{item.name}</h3>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Monthly Impact</span>
                              <span className="font-bold text-green-500">{item.impact}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>COGS Change</span>
                              <span className="font-bold text-green-500">{item.cogs}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Margin Impact</span>
                              <span className="font-bold text-green-500">{item.margin}</span>
                            </div>
                          </div>
                          
                          <div className="pt-2 flex space-x-2">
                            <Button variant="outline" size="sm" className="flex-1">Apply</Button>
                            <Button variant="secondary" size="sm" className="flex-1">Export</Button>
                          </div>
                        </div>
                      </TabsContent>
                    ))}
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Supplier Switch Impact Analysis</CardTitle>
              <CardDescription>Potential financial impact of changing suppliers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Current Supplier</TableHead>
                      <TableHead>New Supplier</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Annual Savings</TableHead>
                      <TableHead>COGS Impact</TableHead>
                      <TableHead>Quality Risk</TableHead>
                      <TableHead>Recommendation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>Metro Foods</TableCell>
                      <TableCell>Fresh Direct</TableCell>
                      <TableCell>Produce</TableCell>
                      <TableCell className="text-green-500">$12,450</TableCell>
                      <TableCell>-0.9%</TableCell>
                      <TableCell>Low</TableCell>
                      <TableCell><Badge className="bg-green-500">Switch</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Seafood Inc</TableCell>
                      <TableCell>Ocean Catch</TableCell>
                      <TableCell>Seafood</TableCell>
                      <TableCell className="text-green-500">$8,300</TableCell>
                      <TableCell>-0.6%</TableCell>
                      <TableCell>Medium</TableCell>
                      <TableCell><Badge className="bg-amber-500">Test First</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Premium Meats</TableCell>
                      <TableCell>Value Butcher</TableCell>
                      <TableCell>Meat</TableCell>
                      <TableCell className="text-green-500">$18,700</TableCell>
                      <TableCell>-1.3%</TableCell>
                      <TableCell>High</TableCell>
                      <TableCell><Badge variant="destructive">Don't Switch</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cash_flow" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Seasonal Cash Flow Prediction</CardTitle>
                <CardDescription>
                  12-month forecast of cash flow needs for ingredient purchasing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={seasonalCashFlowData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`$${value}`, '']} />
                      <Legend />
                      <Bar dataKey="cashFlow" name="Cash Flow" fill="#8884d8" />
                      <Bar dataKey="purchasing" name="Ingredient Purchasing" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Seasonal Planning</CardTitle>
                <CardDescription>
                  Upcoming seasonal purchasing needs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">High-Cost Seasons</p>
                    <div className="grid gap-2">
                      <div className="flex justify-between items-center">
                        <span className="flex items-center"><Calendar className="h-4 w-4 mr-2" />Jul-Aug</span>
                        <Badge className="bg-red-500">$4,100-4,200</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center"><Calendar className="h-4 w-4 mr-2" />Dec-Jan</span>
                        <Badge className="bg-amber-500">$3,200-3,300</Badge>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Recommended Buffer</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Summer (Jul)</p>
                        <p className="text-lg font-bold text-amber-500">$6,500</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Winter (Dec)</p>
                        <p className="text-lg font-bold text-amber-500">$5,200</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button variant="outline" className="w-full">Export Cash Flow Plan</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Advanced Cash Flow Planning</CardTitle>
              <CardDescription>Detailed breakdown by ingredient categories</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Produce', value: 35 },
                          { name: 'Meat', value: 25 },
                          { name: 'Seafood', value: 20 },
                          { name: 'Dairy', value: 15 },
                          { name: 'Dry Goods', value: 5 },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {[
                          { name: 'Produce', value: 35 },
                          { name: 'Meat', value: 25 },
                          { name: 'Seafood', value: 20 },
                          { name: 'Dairy', value: 15 },
                          { name: 'Dry Goods', value: 5 },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value}%`, 'Category Spending']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">Seasonal Purchasing Strategy</h3>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span>Produce</span>
                      <Badge variant="outline" className="font-normal">
                        Buy local Jun-Sep, Import Oct-May
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Seafood</span>
                      <Badge variant="outline" className="font-normal">
                        Substitute species seasonally
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Meat</span>
                      <Badge variant="outline" className="font-normal">
                        Lock in Q4 contracts in August
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button size="sm" className="w-full">Generate Seasonal Planning Report</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Benchmark Tab */}
        <TabsContent value="benchmarks" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Financial KPI Benchmarks</CardTitle>
                <CardDescription>
                  How your performance compares to industry standards
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={benchmarkData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="kpi" type="category" width={100} />
                      <Tooltip formatter={(value) => [`${value}%`, '']} />
                      <Legend />
                      <Bar dataKey="yourValue" name="Your Restaurant" fill="#8884d8" />
                      <Bar dataKey="industryAvg" name="Industry Average" fill="#82ca9d" />
                      <Bar dataKey="benchmark" name="Top Performers" fill="#ffc658" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>KPI Analysis</CardTitle>
                <CardDescription>
                  Key insights from benchmark comparison
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Areas of Concern</p>
                    <div className="grid gap-2">
                      <div className="flex justify-between items-center">
                        <span>COGS</span>
                        <Badge variant="destructive">+4% above avg</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Profit Margin</span>
                        <Badge variant="destructive">-3% below avg</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Food Waste</span>
                        <Badge variant="destructive">+2% above avg</Badge>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Strengths</p>
                    <div className="grid gap-2">
                      <div className="flex justify-between items-center">
                        <span>Labor Cost</span>
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">-2% below avg</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Average Order</span>
                        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">+2% above avg</Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button variant="outline" className="w-full">View Improvement Plan</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Industry Benchmarks by Restaurant Type</CardTitle>
              <CardDescription>Compare your performance across different restaurant categories</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>KPI</TableHead>
                      <TableHead>Your Value</TableHead>
                      <TableHead>Fast Casual</TableHead>
                      <TableHead>Fine Dining</TableHead>
                      <TableHead>Casual Dining</TableHead>
                      <TableHead>Quick Service</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">COGS %</TableCell>
                      <TableCell>32%</TableCell>
                      <TableCell>28%</TableCell>
                      <TableCell>32%</TableCell>
                      <TableCell>30%</TableCell>
                      <TableCell>26%</TableCell>
                      <TableCell><Badge variant="destructive">Above Avg</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Labor Cost %</TableCell>
                      <TableCell>28%</TableCell>
                      <TableCell>30%</TableCell>
                      <TableCell>35%</TableCell>
                      <TableCell>32%</TableCell>
                      <TableCell>25%</TableCell>
                      <TableCell><Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Below Avg</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Profit Margin %</TableCell>
                      <TableCell>15%</TableCell>
                      <TableCell>18%</TableCell>
                      <TableCell>20%</TableCell>
                      <TableCell>15%</TableCell>
                      <TableCell>12%</TableCell>
                      <TableCell><Badge variant="destructive">Below Avg</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Food Waste %</TableCell>
                      <TableCell>7%</TableCell>
                      <TableCell>5%</TableCell>
                      <TableCell>4%</TableCell>
                      <TableCell>6%</TableCell>
                      <TableCell>4%</TableCell>
                      <TableCell><Badge variant="destructive">Above Avg</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Margin Erosion Tab */}
        <TabsContent value="margin_erosion" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Profit Margin Erosion Alerts</CardTitle>
                <CardDescription>
                  Menu items with significant margin decreases
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Menu Item</TableHead>
                        <TableHead>Current Margin</TableHead>
                        <TableHead>Previous Margin</TableHead>
                        <TableHead>Change</TableHead>
                        <TableHead>Primary Cause</TableHead>
                        <TableHead>Recommended Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {marginErosionData.map((item) => (
                        <TableRow key={item.item}>
                          <TableCell className="font-medium">{item.item}</TableCell>
                          <TableCell>{item.currentMargin}%</TableCell>
                          <TableCell>{item.previousMargin}%</TableCell>
                          <TableCell>
                            <Badge variant="destructive" className="flex items-center">
                              <TrendingDown className="h-3 w-3 mr-1" />
                              {item.change}%
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {item.item === 'Grilled Salmon' && 'Salmon price increase (+22%)'}
                            {item.item === 'Beef Tenderloin' && 'Beef wholesale cost (+15%)'}
                            {item.item === 'Truffle Pasta' && 'Truffle oil cost (+8%)'}
                            {item.item === 'Lobster Bisque' && 'Seafood costs (+12%)'}
                            {item.item === 'Wagyu Burger' && 'Wagyu beef costs (+18%)'}
                          </TableCell>
                          <TableCell>
                            {item.item === 'Grilled Salmon' && 'Price increase or substitute'}
                            {item.item === 'Beef Tenderloin' && 'Adjust portion or increase price'}
                            {item.item === 'Truffle Pasta' && 'Find alternate supplier'}
                            {item.item === 'Lobster Bisque' && 'Reduce portion size slightly'}
                            {item.item === 'Wagyu Burger' && 'Blend with regular beef or increase price'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-amber-500" />
                  Alert Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Significant Margin Erosion</p>
                        <p className="text-sm text-amber-700 mt-1">
                          5 menu items have experienced margin erosion of more than 2% in the last 30 days.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-sm text-muted-foreground">Overall Menu Margin</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xl font-bold">65%</span>
                        <Badge variant="destructive" className="text-xs">
                          <TrendingDown className="h-3 w-3 mr-1" />
                          -3%
                        </Badge>
                      </div>
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-sm text-muted-foreground">COGS Impact</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xl font-bold">+2.5%</span>
                        <Badge variant="destructive" className="text-xs">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Negative
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-3">
                    <h4 className="font-medium">Recommended Actions</h4>
                    <div className="grid gap-2">
                      <div className="flex items-start space-x-2">
                        <BadgeDollarSign className="h-4 w-4 text-primary mt-0.5" />
                        <span className="text-sm">Increase prices on all seafood items by 5-8%</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <BadgeDollarSign className="h-4 w-4 text-primary mt-0.5" />
                        <span className="text-sm">Negotiate with beef supplier for better rates</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <BadgeDollarSign className="h-4 w-4 text-primary mt-0.5" />
                        <span className="text-sm">Review portion sizes on high-cost dishes</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button className="w-full">Generate Action Plan</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Margin Erosion Forecast</CardTitle>
              <CardDescription>Projected margin changes over the next quarter based on cost trends</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={[
                        { month: 'Jul', current: 68, projected: 66 },
                        { month: 'Aug', current: 67, projected: 64 },
                        { month: 'Sep', current: 65, projected: 62 },
                        { month: 'Oct', current: 65, projected: 60 },
                        { month: 'Nov', current: 65, projected: 59 },
                        { month: 'Dec', current: 65, projected: 58 },
                      ]}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis domain={[55, 70]} />
                      <Tooltip formatter={(value) => [`${value}%`, '']} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="current" 
                        name="Current Path" 
                        stroke="#ff7300" 
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="projected" 
                        name="With Interventions" 
                        stroke="#82ca9d" 
                        strokeDasharray="5 5" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">Erosion Prevention Strategy</h3>
                  
                  <div className="space-y-3">
                    <div className="bg-muted p-3 rounded-lg">
                      <h4 className="font-medium">Immediate Actions (0-30 days)</h4>
                      <ul className="ml-5 mt-2 text-sm list-disc space-y-1">
                        <li>Increase prices on identified high-erosion items</li>
                        <li>Begin supplier negotiations for seafood and beef</li>
                        <li>Optimize portion sizes without affecting quality</li>
                      </ul>
                    </div>
                    
                    <div className="bg-muted p-3 rounded-lg">
                      <h4 className="font-medium">Medium-Term Actions (30-90 days)</h4>
                      <ul className="ml-5 mt-2 text-sm list-disc space-y-1">
                        <li>Implement new menu pricing structure</li>
                        <li>Finalize new supplier contracts</li>
                        <li>Add alternative high-margin items to menu</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button variant="outline" size="sm" className="w-full">Download Full Strategy</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialPerformanceForecaster;
